import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Maximize2, Minus, Plus, X } from "lucide-react";

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

const ZoomView = ({ width, height, onClose, children }) => {
  const fit = Math.min(1, (window.innerWidth - 16) / width);
  const [z, setZ] = useState(fit);
  const pinch = useRef(null);
  const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const onTouchStart = (e) => { if (e.touches.length === 2) pinch.current = { d: dist(e.touches), z }; };
  const onTouchMove = (e) => {
    if (e.touches.length !== 2 || !pinch.current) return;
    e.preventDefault();
    setZ(clamp(pinch.current.z * (dist(e.touches) / pinch.current.d), fit, 3));
  };
  const step = (k) => setZ((x) => clamp(x * k, fit, 3));

  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col bg-slate-900/95" data-testid="receipt-zoom">
      <div className="flex items-center justify-between px-3 py-2 text-white">
        <span className="text-sm font-semibold">Pinch or use + / − to zoom</span>
        <div className="flex items-center gap-1.5">
          <button onClick={() => step(1 / 1.25)} className="rounded-md bg-white/10 p-2 hover:bg-white/20" aria-label="Zoom out" data-testid="receipt-zoom-out"><Minus className="h-4 w-4" /></button>
          <span className="w-12 text-center font-num text-xs" data-testid="receipt-zoom-level">{Math.round((z / fit) * 100)}%</span>
          <button onClick={() => step(1.25)} className="rounded-md bg-white/10 p-2 hover:bg-white/20" aria-label="Zoom in" data-testid="receipt-zoom-in"><Plus className="h-4 w-4" /></button>
          <button onClick={onClose} className="ml-2 rounded-md bg-white p-2 text-plum-950" aria-label="Close" data-testid="receipt-zoom-close"><X className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="flex-1 overflow-auto p-2" style={{ touchAction: "pan-x pan-y" }} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={() => (pinch.current = null)}>
        <div style={{ width: width * z, height: height * z }} className="mx-auto overflow-hidden">
          <div style={{ width, transform: `scale(${z})`, transformOrigin: "top left" }} className="pointer-events-none">{children}</div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export const FitWidth = ({ width = 640, children }) => {
  const outer = useRef(null);
  const inner = useRef(null);
  const [box, setBox] = useState({ s: 1, h: "auto", ih: 0 });
  const [zoom, setZoom] = useState(false);

  useLayoutEffect(() => {
    const fit = () => {
      const s = Math.min(1, outer.current.clientWidth / width);
      setBox({ s, h: s < 1 ? inner.current.offsetHeight * s : "auto", ih: inner.current.offsetHeight });
    };
    const ro = new ResizeObserver(fit);
    ro.observe(outer.current);
    ro.observe(inner.current);
    fit();
    return () => ro.disconnect();
  }, [width]);

  const scaled = box.s < 1;
  return (
    <>
      {scaled && (
        <button onClick={() => setZoom(true)} className="no-print mb-2 flex w-full items-center justify-center gap-1.5 rounded-md bg-plum-900 py-2 text-xs font-semibold text-white" data-testid="receipt-zoom-btn">
          <Maximize2 className="h-3.5 w-3.5" />Tap the receipt to zoom
        </button>
      )}
      <div ref={outer} className="fit-outer relative w-full overflow-hidden" style={{ height: box.h }} data-testid="fit-width">
        <div ref={inner} data-fit className="fit-inner"
          style={scaled ? { width, transform: `scale(${box.s})`, transformOrigin: "top left" } : { minWidth: width }}>
          {children}
        </div>
        {scaled && <button onClick={() => setZoom(true)} className="no-print absolute inset-0" aria-label="Open receipt full screen" data-testid="receipt-zoom-open" />}
        {zoom && <ZoomView width={width} height={box.ih} onClose={() => setZoom(false)}>{children}</ZoomView>}
      </div>
    </>
  );
};
